import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { apiClient, getErrorMessage } from '../../packages/api-client';
import { useAuth } from '../../packages/auth-client';
import { getUsersPaginated, type User } from '../../packages/api-client/pm-api';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Dialog } from '../../packages/ui';
import { PageSkeleton } from '../../components/Skeleton';

// The API's largest page (backend pagination.MaxLimit); a larger request is
// silently served 20.
const USER_LIMIT = 100;

/**
 * DesignateSuperAdminPage — superadmin-only.
 *
 * The post-000042 policy is "exactly one global superadmin." This page is
 * the sanctioned way to transfer that role to someone else without
 * dropping into the `cmd/grantsuperadmin` CLI. Calls
 * `POST /users/superadmin/transfer` (demote-self then promote-target, two
 * writes with a best-effort restore on failure — not one transaction),
 * then signs this browser out. The demotion stamps tokens_revoked_at, so every
 * access JWT the actor already holds — including impersonation tokens they
 * minted — is refused on its next request on the instance that served the
 * transfer and within 5s (RevocationRefreshInterval) on the others.
 */
export function DesignateSuperAdminPage() {
    usePageTitle('Designate Superadmin');
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [users, setUsers] = useState<User[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [target, setTarget] = useState<User | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        // One page: there's typically only a small number of active admins
        // to choose from. The page says when the list is cut off.
        getUsersPaginated(1, USER_LIMIT)
            .then(r => {
                setUsers((r.data ?? []).filter(u => u.is_active && u.id !== user?.user_id));
                setTotal(r.meta?.total ?? 0);
            })
            .catch(() => { /* leave list empty; the UI shows a hint */ })
            .finally(() => setLoading(false));
    }, [user?.user_id]);

    const filtered = query.trim()
        ? users.filter(u => {
            const q = query.toLowerCase();
            return u.email.toLowerCase().includes(q)
                || `${u.first_name} ${u.last_name}`.toLowerCase().includes(q);
        })
        : users;

    async function handleTransfer() {
        if (!target) return;
        setSubmitting(true);
        try {
            await apiClient.post('/users/superadmin/transfer', { to_user_id: target.id });
            toast.success(`Superadmin transferred to ${target.email}. You will be signed out.`);
            // Best-effort logout to discard the now-stale JWT (it still
            // carries is_superadmin: true). The user logs back in as a
            // tenant member.
            try { await logout(); } catch { /* ignore */ }
            setTimeout(() => navigate('/login', { replace: true }), 800);
        } catch (err) {
            toast.error(getErrorMessage(err));
            setSubmitting(false);
        }
    }

    return (
        <div>
            <div style={{ marginBottom: '1.5rem' }}>
                <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--color-text-primary)', margin: 0 }}>
                    Designate Superadmin
                </h1>
                <p style={{ color: 'var(--color-text-secondary)', marginTop: '0.5rem', maxWidth: 720 }}>
                    Transfer the global superadmin role to another active user. This is irreversible from the
                    UI — once transferred, you will be signed out and the new superadmin will be the only one
                    who can transfer it back. Recovery path of last resort is the <code>cmd/grantsuperadmin</code> CLI.
                </p>
            </div>

            {/* Current holder */}
            <div
                style={{
                    padding: '0.75rem 1rem',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    background: 'var(--color-surface)',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                }}
            >
                <span style={{ fontSize: 18 }}>⚡</span>
                <div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>Current superadmin</div>
                    <div style={{ fontWeight: 600 }}>{user?.email}</div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
                <input
                    type="search"
                    placeholder="Filter by email or name"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    style={{ flex: 1, padding: '0.5rem 0.75rem', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, color: 'var(--color-text-primary)', fontSize: '0.85rem' }}
                />
            </div>

            {!loading && total > USER_LIMIT && (
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                    Showing the first {USER_LIMIT} of {total} users. Filter by email to find someone not listed here.
                </p>
            )}

            {loading ? (
                <PageSkeleton />
            ) : filtered.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                    {users.length === 0
                        ? 'No other active users exist yet — invite someone first.'
                        : 'No active users match that filter.'}
                </div>
            ) : (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-2)' }}>
                                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Name</th>
                                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Email</th>
                                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Role</th>
                                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 600 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((u, i) => (
                                <tr key={u.id} style={{ borderBottom: i < filtered.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                                    <td style={{ padding: '0.6rem 0.75rem' }}>
                                        {u.first_name || u.last_name ? `${u.first_name} ${u.last_name}`.trim() : '—'}
                                    </td>
                                    <td style={{ padding: '0.6rem 0.75rem', color: 'var(--color-text-secondary)' }}>{u.email}</td>
                                    <td style={{ padding: '0.6rem 0.75rem', color: 'var(--color-text-secondary)' }}>
                                        {(u.roles ?? []).join(', ') || '—'}
                                    </td>
                                    <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            aria-label={`Transfer to ${u.email}`}
                                            onClick={() => setTarget(u)}
                                        >
                                            Transfer to…
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog
                open={target !== null}
                onClose={() => { if (!submitting) setTarget(null); }}
                closeOnBackdrop={false}
                title={target ? `Transfer superadmin to ${target.email}?` : ''}
                footer={<>
                    <button type="button" className="btn btn-secondary btn-md" disabled={submitting} onClick={() => setTarget(null)}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-danger btn-md" disabled={submitting} onClick={handleTransfer}>
                        {submitting ? 'Transferring…' : 'Transfer and sign out'}
                    </button>
                </>}
            >
                {target && (
                    <ul style={{ margin: 0, paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <li>
                            <strong>{`${target.first_name} ${target.last_name}`.trim() || target.email}</strong> ({target.email}) becomes
                            the only global superadmin. You lose superadmin access, and only they can transfer it back.
                        </li>
                        <li>This browser is signed out immediately. They must sign in again to receive the elevation.</li>
                        <li>
                            Sessions you have open in other tabs or devices, and any impersonation sessions you started, stop
                            working on their next request — within 5 seconds on every server.
                        </li>
                        <li>
                            If promoting them fails after you are demoted, your access is restored where possible; the
                            recovery of last resort is the <code>cmd/grantsuperadmin</code> CLI.
                        </li>
                    </ul>
                )}
            </Dialog>
        </div>
    );
}
